import { useCallback } from 'react';
import { useWidgetStore } from '../store';
import { Suggestion, ConversationItem } from '../store/types';
import { normalizeActionUrl } from '../utils/normalizeActionUrl';

export const useSuggestionSystem = (debug: boolean = false) => {
    const conversationItems = useWidgetStore((state) => state.conversationItems);
    const messages = useWidgetStore((state) => state.messages);

    const log = useCallback((...args: any[]) => {
        if (debug) {
            console.log('[RemstalVoice:useSuggestionSystem]', ...args);
        }
    }, [debug]);

    const handleMarkAsSuggested = useCallback(async (suggestionData: any) => {
        const { type, title, description, action_url, action_label } = suggestionData;
        log('🔧 Mark as suggested called with:', { type, title, description, action_url, action_label });

        // The agent emits action_url freely (may be scheme-less, relative, or markdown-wrapped).
        // window.open() on such a string resolves against the embedding origin and opens a blank
        // page, so coerce/validate here and drop the OPEN button when it cannot be made absolute.
        let safeUrl: string | undefined;
        if (action_url !== undefined && action_url !== null && action_url !== '') {
            const normalized = normalizeActionUrl(action_url);
            if (normalized.ok) {
                safeUrl = normalized.url;
            } else {
                console.warn('[RemstalVoice] dropped unsafe action_url from mark_as_suggested', {
                    action_url,
                    reason: normalized.reason,
                });
            }
        }

        const suggestion: Suggestion = {
            type: type || 'info',
            title: title || 'Suggestion',
            description: description || 'The assistant has a suggestion for you.',
            action_url: safeUrl,
            action_label: action_label,
            id: Date.now().toString(),
        };

        const conversationItem: ConversationItem = {
            type: 'suggestion',
            id: Date.now().toString(),
            timestamp: Date.now(),
            suggestion: suggestion,
        };

        useWidgetStore.getState().addConversationItem(conversationItem);
        log('✅ Added suggestion as conversation item:', suggestion);
    }, [log, conversationItems]);

    const addTestSuggestion = useCallback(() => {
        log('🧪 Adding test suggestion');
        if (messages.length === 0 || messages[messages.length - 1].author !== 'agent') {
            useWidgetStore.getState().addMessage({
                text: 'Hier sind einige Empfehlungen für Sie:',
                author: 'agent',
                id: Date.now().toString(),
            });
        }

        setTimeout(() => {
            handleMarkAsSuggested({
                type: 'location',
                title: 'Weinstadt Stadtpark',
                description: 'Schöner Park zum Spazieren mit Spielplatz für Kinder',
                action_url: 'https://maps.google.com/?q=Weinstadt+Stadtpark',
            });
        }, 100);
    }, [handleMarkAsSuggested, log, messages]);

    return { handleMarkAsSuggested, addTestSuggestion };
};
